import { v4 as uuid } from 'uuid';
import { waitForRequest } from '../indexedDB';
import { acceptedIdentity, applySubmissionIdentity } from './identity';
import {
  hasPostStarted,
  makeQueueRecord,
  OWNERSHIP_DURATION_MS,
  validateQueueRecordInput,
  withQueueStore,
} from './storage';

const keyOf = ({ apiUrl, submissionId }) => [apiUrl, submissionId];
const contextKey = (headers = {}) =>
  JSON.stringify(
    Object.entries(headers)
      .filter(([key]) => /^X-(SDI|SCI|EDI|ECI|LDI|LCI|WDI|WCI|PNDI|PNCI|WWID)$/.test(key))
      .sort(),
  );
const skipped = (reason) => ({ status: 'skipped', reason });

// A failed online final keeps the session closed to incomplete work and drafts,
// but the user may submit the final again. Offline and acknowledged finals stay closed.
const isFinalized = (previous, input) =>
  previous?.finalized &&
  !(input.kind === 'final' && previous.finalRoute === 'online' && previous.acknowledgedRevision !== previous.revision);

function sessionState(input, previous, route) {
  return {
    ...previous,
    apiUrl: input.apiUrl,
    submissionId: input.submissionId,
    contextKey: contextKey(input.headers),
    nodeId: input.nodeId,
    flowId: input.flowId,
    feedbackId: input.feedbackId,
    initialFeedbackId: previous?.initialFeedbackId || input.feedbackId,
    version: input.version,
    revision: input.revision,
    finalized: input.kind === 'final',
    finalRoute: input.kind === 'final' ? route : null,
    updatedAt: Date.now(),
  };
}

async function removeUnsentIncomplete(store, input) {
  const record = await waitForRequest(store.index('bySubmissionKind').get([...keyOf(input), 'incomplete']));

  if (record && !hasPostStarted(record)) await waitForRequest(store.delete(record.id));
}

/** Save the answer, remove pending incomplete work and seal a final atomically. */
export function saveQueueSnapshot(input) {
  validateQueueRecordInput(input);

  return withQueueStore(
    'readwrite',
    async (store, owners, sessions) => {
      const previous = await waitForRequest(sessions.get(keyOf(input)));
      const saved = await waitForRequest(store.index('bySubmissionKind').get([...keyOf(input), input.kind]));

      if (isFinalized(previous, input)) return skipped('finalized');
      if (previous?.revision >= input.revision || saved?.revision >= input.revision) return skipped('obsolete');
      if (
        saved?.postStartedRevision != null &&
        JSON.stringify(saved.payload.responses) === JSON.stringify(input.payload.responses)
      ) {
        return skipped('unchanged');
      }

      const prepared = applySubmissionIdentity(input, previous);
      const record = saved
        ? {
            ...saved,
            payload: prepared.payload,
            headers: prepared.headers,
            feedbackId: prepared.feedbackId,
            version: prepared.version,
            revision: prepared.revision,
            captcha: prepared.captcha,
            status: saved.status === 'preparing' || hasPostStarted(saved) ? 'pending' : saved.status,
            updatedAt: Date.now(),
          }
        : makeQueueRecord(prepared);

      if (input.kind === 'final') await removeUnsentIncomplete(store, input);

      await waitForRequest(saved ? store.put(record) : store.add(record));
      await waitForRequest(sessions.put(sessionState(prepared, previous, 'offline')));

      return {
        status: 'queued',
        submissionId: input.submissionId,
        recordId: record.id,
        revision: record.revision,
        feedbackId: record.feedbackId,
        version: record.version,
      };
    },
    true,
  );
}

/** Only sessions already using the queue need this lease for direct online work. */
export function claimOnlineSubmission(input) {
  validateQueueRecordInput(input);

  return withQueueStore(
    'readwrite',
    async (store, owners, sessions) => {
      const previous = await waitForRequest(sessions.get(keyOf(input)));
      if (isFinalized(previous, input)) return skipped('finalized');
      if (previous?.revision >= input.revision) return skipped('obsolete');

      const owner = await waitForRequest(owners.get(keyOf(input)));
      if (owner?.expiresAt > Date.now()) return null;

      const prepared = applySubmissionIdentity(input, previous);
      const ownership = { ownerId: uuid(), expiresAt: Date.now() + OWNERSHIP_DURATION_MS };

      // Preparation holds the lease without discarding the recoverable answer.
      await waitForRequest(
        owners.put({
          ...ownership,
          apiUrl: input.apiUrl,
          submissionId: input.submissionId,
          recordId: null,
          revision: input.revision,
          previousRevision: previous?.revision ?? null,
        }),
      );

      return { ...prepared, id: null, ownership };
    },
    true,
  );
}

/** Commit the online transition only after preparation, immediately before POST. */
export function beginOnlineSubmissionPost(attempt) {
  return withQueueStore(
    'readwrite',
    async (store, owners, sessions) => {
      const owner = await waitForRequest(owners.get(keyOf(attempt)));
      const previous = await waitForRequest(sessions.get(keyOf(attempt)));

      if (
        owner?.ownerId !== attempt.ownership.ownerId ||
        owner.recordId !== null ||
        owner.revision !== attempt.revision ||
        owner.expiresAt <= Date.now() ||
        (previous?.revision ?? null) !== owner.previousRevision ||
        isFinalized(previous, attempt) ||
        previous?.revision >= attempt.revision
      ) {
        return false;
      }

      await removeUnsentIncomplete(store, attempt);
      await waitForRequest(sessions.put(sessionState(attempt, previous, 'online')));

      return true;
    },
    true,
  );
}

/** Acknowledge only this lease; never delete a newer answer or recreate a record. */
export function acknowledgeSubmission(attempt, body) {
  return withQueueStore(
    'readwrite',
    async (store, owners, sessions) => {
      const owner = await waitForRequest(owners.get(keyOf(attempt)));

      if (
        owner?.ownerId !== attempt.ownership.ownerId ||
        owner.revision !== attempt.revision ||
        owner.expiresAt <= Date.now()
      ) {
        return false;
      }

      const previous = await waitForRequest(sessions.get(keyOf(attempt)));
      const identity = acceptedIdentity(attempt, body);

      let version = Math.max(previous?.version || 0, identity.version);
      const records = await waitForRequest(store.index('bySubmission').getAll(keyOf(attempt)));

      for (const record of records) {
        if (record.id === attempt.id && record.revision === attempt.revision) {
          await waitForRequest(store.delete(record.id));
        } else if (record.revision > attempt.revision && !hasPostStarted(record)) {
          const prepared = applySubmissionIdentity(record, identity);
          await waitForRequest(store.put({ ...record, ...prepared }));
          version = Math.max(version, prepared.version || 0);
        }
      }

      if (previous) {
        await waitForRequest(
          sessions.put({ ...previous, ...identity, version, acknowledgedRevision: attempt.revision }),
        );
      }

      return true;
    },
    true,
  );
}

/** Include the session lease when a replacement record has no lease of its own. */
export function listQueuePreparationRecords(apiUrl) {
  if (typeof apiUrl !== 'string' || !apiUrl.trim()) {
    throw new TypeError('Queue reads require an API environment');
  }

  return withQueueStore(
    'readonly',
    async (store, owners) => {
      const records = await waitForRequest(store.index('byApiUrl').getAll(apiUrl));

      return Promise.all(
        records.map(async (record) => {
          const owner = await waitForRequest(owners.get(keyOf(record)));

          return { ...record, sessionOwnershipExpiresAt: owner?.expiresAt || 0 };
        }),
      );
    },
    true,
  );
}

/** A sent/uncertain revision and a final are never automatic drafts. */
export function readIncompleteSession({ apiUrl, nodeId, flowId, submissionId, feedbackId, headers }) {
  return withQueueStore(
    'readonly',
    async (store, owners, sessions) => {
      const candidates = await waitForRequest(sessions.index('bySurvey').getAll([apiUrl, nodeId, flowId]));
      const session = candidates
        .filter(
          (candidate) =>
            (!submissionId || candidate.submissionId === submissionId) &&
            (!feedbackId ||
              [candidate.feedbackId, candidate.initialFeedbackId, candidate.submissionId].includes(feedbackId)) &&
            (headers === undefined || candidate.contextKey === contextKey(headers)),
        )
        .sort((a, b) => b.updatedAt - a.updatedAt)[0];

      if (!session) return null;
      if (session.finalized) return { session, record: null };

      const record = await waitForRequest(store.index('bySubmissionKind').get([...keyOf(session), 'incomplete']));

      return {
        session,
        record: record && !hasPostStarted(record) ? record : null,
        uncertain: record ? hasPostStarted(record) : session.acknowledgedRevision !== session.revision,
      };
    },
    true,
  );
}
